import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../context/AuthContext";
import { API } from "../../constant";
import { setToken } from "../../helpers";
import "./Login.css";
import Loading from "../Hooks/Loading";
import { loadCaptchaEnginge, LoadCanvasTemplate, validateCaptcha } from 'react-simple-captcha';
import { useForm } from "../../zustand/useForm";
import logo2 from "../../Media/LogoReverseWithTag.svg";

function Login() {

    const navigate = useNavigate();
    const { setUser } = useAuthContext();
    const [isLoading, setIsLoading] = useState(false); // Using this for loading state
    const [error, setError] = useState("");
    const [userCaptcha, setUserCaptcha] = useState('');

    // Destructure zustand state and setter
    const { formData, setFormdata } = useForm();
    // Initialize local form state from zustand data
    const [form, setForm] = useState(formData); 
    
    // Effect to load captcha on component mount
    useEffect(() => {
        loadCaptchaEnginge(6, 'transparent', '#fff');
    }, []);

    // Effect to keep local form state in sync with zustand's formData when it changes
    // This is good for "Remember Me" logic when component mounts with saved data.
    useEffect(() => {
        setForm(formData);
    }, [formData]);


    const handleFormValue = (e) => {
        const input = e.target;
        const name = input.name;
        // Correctly handle checkbox value: input.checked for 'rememberMe', input.value for others
        const value = name === 'rememberMe' ? input.checked : input.value;
        setForm({
            ...form,
            [name]: value
        });
    };
    
    // CAPTCHA VALIDATION FUNCTION
    const validateUserCaptcha = () => {
        if (validateCaptcha(userCaptcha) === true) {
            // Captcha is valid - don't reload yet, will proceed to login
            setUserCaptcha(''); // Clear user input
            return true;
        } else {
            // Show alert for failure and reload captcha
            alert('Captcha Does Not Match');
            loadCaptchaEnginge(6, 'transparent', '#fff');
            setUserCaptcha(''); // Clear user input
            return false;
        }
    };
    
    // MAIN SUBMISSION HANDLER
    const handleSubmit = async (e) => { // ⭐ ADDED 'async' HERE
        e.preventDefault();
        setError(""); // Clear previous errors
        
        // 1. Validate Captcha FIRST
        if (!validateUserCaptcha()) {
            return; // Stop submission if captcha fails
        }

        // 2. Handle Remember Me state with zustand
        if (form.rememberMe) {
            setFormdata(form); // Save the current form to zustand
        } else {
            // Clear credentials in zustand if 'Remember Me' is unchecked
            setFormdata({
                username: '',
                password: '',
                rememberMe: false
            });
        }
 
        try {
            setIsLoading(true); // Start loading

            const value = {
                identifier: form.username,
                password: form.password,
            };

            // ⭐ CRITICAL FIX 1: 'await' the fetch call ⭐
            const response = await fetch(`${API}/auth/local`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(value),
            });
            
            // ⭐ CRITICAL FIX 2: 'await' the response.json() call ⭐
            const data = await response.json(); 
            
            if (data?.error) {
                // Handle Strapi/API errors (e.g., invalid credentials)
                throw new Error(data.error.message || "Login failed.");
            }

            // Authentication Successful
            console.log("Login successful! User data:", data.user);

            // Clear any error messages
            setError("");

            // Set the JWT token. Pass 'form.rememberMe' (instead of rememberMe state)
            // since 'form' holds the latest value.
            setToken(data.jwt, form.rememberMe);

            // set the user
            setUser(data.user);

            // Check if user has seen intro screen
            const hasSeenIntro = localStorage.getItem(`intro_seen_${data.user.id}`);

            // Redirect to intro on first login, otherwise to home
            if (!hasSeenIntro) {
                console.log("First login - navigating to intro screen");
                navigate("/intro");
            } else {
                console.log("Returning user - navigating to main screen");
                navigate("/");
            }
            // Navigation will trigger re-render automatically, no reload needed

        } catch (err) {
            console.error(err);
            // Handle network errors or thrown API errors
            if (err.message.includes("Failed to fetch")) {
                setError("Network error. Please check your connection or server status.");
            } else {
                // Use the error message from the thrown error
                setError(err.message || "An unexpected error occurred.");
            }

            // Clear captcha input
            setUserCaptcha('');
        } finally {
            setIsLoading(false); // Stop loading regardless of outcome

            // Reload captcha after form is visible again (after loading state is false)
            setTimeout(() => {
                try {
                    loadCaptchaEnginge(6, 'transparent', '#fff');
                } catch (captchaError) {
                    console.warn("Failed to reload captcha:", captchaError);
                }
            }, 100);
        }
    };

    const showPassword = (e) => {
        const input = e.target.previousSibling;
        if (input.type === "password") {
            input.type = "text";
            e.target.classList.remove("hide");
        } else {
            input.type = "password";
            e.target.classList.add("hide");
        }
    };
    
    return (
        <div className="login-register login-area-inner">
            {isLoading && <Loading />}

            {!isLoading && (
                <>
                    <div className="login-register-head mb---30 fade-in-down">
                        <img src={logo2} alt="login-register"/>
                    </div>
                    <div className="theme-form">
                {/* handleSubmit is the only handler for form submission */}
                <form onSubmit={handleSubmit}>
                    <label className="input-field">
                        <input 
                        className="field-with-icon" 
                        type="text" name="username" 
                        placeholder="username@mail.com" 
                        required 
                        value={form.username}
                        onChange={handleFormValue}
                        />
                    </label>
                    <label className="input-field">
                        <input className="field-with-icon" 
                        type="password" name="password" 
                        placeholder="**********" 
                        required 
                        value={form.password}
                        onChange={handleFormValue}
                        />
                        <span onClick={showPassword} className="eye-icon hide"></span>
                    </label>

                    <span className="captcha login-captcha">
                        <LoadCanvasTemplate />
                        <label className="captcha-feld ">
                            <input
                                placeholder="Enter Captcha Value"
                                id="user_captcha_input"
                                name="user_captcha_input"
                                type="text"
                                value={userCaptcha}
                                onChange={(e) => setUserCaptcha(e.target.value)}
                            />
                        </label>
                    </span>
                    
                     <span className="flex-wrap">
                        <span className="checkbox">
                            <input 
                                type="checkbox" 
                                name="rememberMe" 
                                checked={form.rememberMe} 
                                onChange={handleFormValue}
                            />
                            Remember Me
                        </span>
                    </span>
                    
                    {/* The type="submit" will automatically call the form's onSubmit (handleSubmit) */}
                    <button className="btn btn-gradient btn-full btn-white mb---10 btn-center delay-2" type="submit" disabled={isLoading}>Login</button>
                </form>
                {error && <div id="errorMessage">{error}</div>}
                <div className="btn btn-link btn-white ctr mt---20 delay-2">
                    <p className="hover-bounce mb---10"><Link to="/forgot-password">Forgot Password</Link></p>
                    <p className="hover-bounce"><Link to="/register-intro">Sign Up</Link></p>
                </div>
            </div>
                </>
            )}
        </div>
    );
}

export default Login;